import React, { useMemo, useContext, useCallback } from "react";
import ValidationError from "../Pricing/AddToCart/ValidationError";
import {
  ConfigurationOption,
  ProductDetailGroupingSelect,
} from "../ProductDetailPageResult";
import GroupConfigurationSelect from "./GroupConfigurationSelect";
import { processValidOptions } from "./VirtualGroupingHelper";
import { VirtualGroupingContext } from "../useVirtualGroupingContext";
import { User } from "~/common/Store/models/User";

interface Props {
  productGroupingSelect: ProductDetailGroupingSelect[];
  addToCartError?: ValidationError;
  selectedOptions: readonly ConfigurationOption[];
  handleClickVirtualGroupingVariation: (
    name: string,
    value: string,
    validOption: boolean
  ) => void;
  user: User;
  showPricingInDropdown?: boolean;
}

const ProductDetailVirtualGroupingSelect = ({
  addToCartError,
  handleClickVirtualGroupingVariation,
  selectedOptions,
  productGroupingSelect,
  user,
  showPricingInDropdown = true,
}: Props) => {
  const { virtualGroupOptionsMap } = useContext(VirtualGroupingContext);

  const hasMoreThanOneDropdown = virtualGroupOptionsMap.length > 1;

  // Used to gray out options in dropdowns if they are not valid
  const getValidOptions = useCallback(
    (selectedOptions: readonly ConfigurationOption[], name: string) => {
      /* if no filter selected, show allow all options
      if some are filters are selected, get items that have config options that match all the selected options
      minus the type we're looking at then get the options with this type from these items. */
      const selectedOptionsToFilter = selectedOptions.filter(
        (option) => option.name !== name
      );

      const validOptions = processValidOptions(
        productGroupingSelect,
        selectedOptionsToFilter,
        name
      );

      return validOptions;
    },
    [productGroupingSelect]
  );

  const virtualGroupingOptions = useMemo(
    () =>
      virtualGroupOptionsMap.map((options) => {
        return {
          name: options.name,
          variations: options.variations,
          validOptions: getValidOptions(selectedOptions, options.name),
        };
      }),
    [getValidOptions, selectedOptions, virtualGroupOptionsMap]
  );

  return (
    <div className="virtual-grouping">
      <p className="virtualGroupingHeading">
        <span className="font-bold">Product Options</span>
      </p>
      <div className="configure-contain form">
        {virtualGroupingOptions.map((options) => (
          <GroupConfigurationSelect
            dropdown={options}
            handleClickVirtualGroupingVariation={
              handleClickVirtualGroupingVariation
            }
            selectedOptions={selectedOptions}
            showError={addToCartError?.sourceName === "vgSelection"}
            key={options.name}
            user={user}
            hasMoreThanOneDropdown={hasMoreThanOneDropdown}
            showPricingInDropdown={showPricingInDropdown}
          />
        ))}
      </div>
    </div>
  );
};

export default ProductDetailVirtualGroupingSelect;
